import { toast } from "@/components/ui/use-toast";
import { useMutation, useQueryClient } from "@tanstack/react-query";

interface MutationOptions<T> {
  method?: "POST" | "PUT" | "DELETE";
  id: string; // Cette valeur est utilisée pour générer la clé dynamique
  onSuccess?: (data: T) => void;
  onError?: (error: Error) => void;
}

export function useMutateData<T>({ method = "POST", id, onSuccess, onError }: MutationOptions<T>) {
  const queryClient = useQueryClient();

  const mutateData = async ({ endpoint, body }: { endpoint: string, body: any }) => {
    let token = localStorage.getItem("jwtToken");
    if (!token) throw new Error("No token available");

    const response = await fetch(endpoint, {
      method,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });

    if (response.status === 401) {
      token = await refreshToken();
      if (token) {
        return mutateData({ endpoint, body });
      } else {
        toast({ title: "Votre session a expiré, veuillez vous reconnecter." });
        window.location.href = "/logout";
        return;
      }
    }

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return response.json();
  };

  const refreshToken = async () => {
    const refreshToken = localStorage.getItem("refreshToken");
    if (!refreshToken) throw new Error("No refresh token available");

    const response = await fetch(
      `${import.meta.env.VITE_API_ENDPOINT}/token/refresh`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${refreshToken}`,
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      }
    );

    if (!response.ok) throw new Error("Failed to refresh token");

    const data = await response.json();
    const newToken = data.token;
    const newRefreshToken = data.refresh_token;

    localStorage.setItem("jwtToken", newToken);
    localStorage.setItem("refreshToken", newRefreshToken);

    return newToken;
  };
  
  return useMutation<T, Error, { endpoint: string, body: any }>({
    mutationFn: mutateData,
    onSuccess: (data) => {
        // Invalidation de la query avec une clé dynamique
        queryClient.invalidateQueries([id]); // Utilise `id` pour invalider la bonne query
        onSuccess?.(data);
    },
    onError: (error) => {
      toast({ title: "Une erreur est survenue.", description: error.message });
      onError?.(error);
    },
  });
}
